// src/components/common/SubmittedPersonModal.tsx
/**
 * Read-only detail for one person staged on a **public submission** — a care-request subject or a
 * course-intake participant.
 *
 * ⚠️ **Everything here comes from the model view the page already fetched.** There is deliberately no
 * member lookup: `CareRequestSubjectView` and `IntakeParticipantView` carry the person's own email,
 * phone, gender, date of birth and matched-member name, so going back to `memberService` would add a
 * request per person to re-fetch what is already in hand — and would show the *member's* details where
 * the point of this screen is to show **what was submitted**. The two can differ, and the difference is
 * exactly what an admin is checking before approving.
 *
 * A person here is not necessarily a member yet: `memberID` is set only once the API matched them, and
 * the record itself is created on approval. The notice says which case applies.
 */
import { Alert, Badge, Text } from '@mantine/core';
import {
  IconBuildingChurch, IconCake, IconGenderBigender, IconHeartHandshake, IconInfoCircle, IconMail,
  IconPhone, IconPhoneCall, IconUser, IconUserQuestion,
} from '@tabler/icons-react';
import {
  MemberDetailModal, formatBirthDate, type MemberDetailFields, type MemberDetailRow,
} from './MemberDetailModal';

/** The subset of `CareRequestSubjectView` / `IntakeParticipantView` this modal reads. */
export interface SubmittedPerson {
  /** Stable key for the row — `subjectID` or `participantID`. */
  id: number;
  firstName?: string | null;
  surname?: string | null;
  email?: string | null;
  phone?: string | null;
  gender?: string | null;
  /** ISO string, staged per person since SDK 0.0.49. */
  dateOfBirth?: string | null;
  /** "Husband" / "Wife" / "Parent" … on care requests; absent on intakes. */
  role?: string | null;
  /**
   * Affiliation label, computed server-side (SDK 0.0.56): a member's location name, otherwise the
   * church they named, otherwise "Not a member".
   *
   * ⚠️ Render this rather than deriving it from `isMember` / `otherChurch` / `locationName`. The
   * server owns the wording so the intake and request pages cannot drift apart, and the raw fields
   * are kept below only for the rows that genuinely need them.
   */
  displayLocationName?: string | null;
  /** Another church they attend. Superseded by `displayLocationName` for display. */
  otherChurch?: string | null;
  /** Self-declared "I already attend here". Superseded by `displayLocationName` for display. */
  isMember?: boolean;
  /** Second contact number. On the intake record for the main applicant only. */
  altContact?: string | null;
  /** How the requester asked to be contacted. Care requests, main requester only. */
  preferredContactMethod?: string | null;
  isMain?: boolean;
  /** Set once the API matched this person to an existing member. */
  memberID?: number | null;
  /** Matched member name, resolved by the API. */
  memberName?: string | null;
  /**
   * True when the API carries **only a name** for this person.
   *
   * ⚠️ Set for the on-behalf-of subject on a care request: that person lives in the top-level
   * `subjectFirstName` / `subjectSurname` block, which has no email, phone, gender or role. Without
   * this flag the modal would render a grid of "Not on record" that reads as a data-entry gap rather
   * than a limit of the API.
   */
  nameOnly?: boolean;
}

export interface SubmittedPersonModalProps {
  person: SubmittedPerson | null;
  opened: boolean;
  onClose: () => void;
  /** "request" or "intake". Drives the wording only. */
  contextLabel: string;
  /** Consent is recorded on the submission as a whole, not per person. */
  hasConsent?: boolean;
}

export default function SubmittedPersonModal({
  person, opened, onClose, contextLabel, hasConsent,
}: SubmittedPersonModalProps) {
  if (!person) {
    return <MemberDetailModal member={null} opened={opened} onClose={onClose} />;
  }

  const submittedName = [person.firstName, person.surname].filter(Boolean).join(' ').trim();
  const isMatched = person.memberID != null;

  /** Straight from the model view — the header, badges and avatar initials read off this. */
  const fields: MemberDetailFields = {
    memberID: person.memberID ?? null,
    firstName: person.firstName,
    lastName: person.surname,
    email: person.email,
    cellNumber: person.phone,
    altContact: person.altContact,
    gender: person.gender,
    dateOfBirth: person.dateOfBirth,
    isMain: person.isMain,
    hasConsent,
    // The badge slot is generic, so a request's role ("Husband", "Wife") reads well in it.
    typeName: person.role,
  };

  /**
   * Rows built from the submission itself.
   *
   * Contact and date of birth are **always shown**, even when blank: a vanishing row is
   * indistinguishable from a missing feature, and an admin looking for a phone number needs to see
   * that it was asked for and left empty. The rest appear only when the submission carries them.
   */
  const rows: MemberDetailRow[] = person.nameOnly ? [] : [
    { icon: <IconMail size={18} />, label: 'Email', value: person.email },
    { icon: <IconPhone size={18} />, label: 'Phone', value: person.phone },
    ...(person.altContact
      ? [{ icon: <IconPhoneCall size={18} />, label: 'Alternative contact', value: person.altContact }]
      : []),
    ...(person.preferredContactMethod
      ? [{
        icon: <IconMail size={18} />,
        label: 'Preferred contact',
        value: person.preferredContactMethod,
      }]
      : []),
    { icon: <IconGenderBigender size={18} />, label: 'Gender', value: person.gender },
    {
      icon: <IconCake size={18} />,
      label: 'Date of birth',
      value: person.dateOfBirth
        ? (formatBirthDate(person.dateOfBirth) ?? person.dateOfBirth)
        : null,
    },
    ...(person.role
      ? [{ icon: <IconHeartHandshake size={18} />, label: 'Role', value: person.role }]
      : []),
    ...(person.displayLocationName
      ? [{
        icon: <IconBuildingChurch size={18} />,
        label: 'Affiliation',
        // Server-computed: location name, other church, or "Not a member". Rendered as-is.
        value: (
          <Badge size="sm" variant="light" color={person.isMember ? 'teal' : 'gray'}>
            {person.displayLocationName}
          </Badge>
        ),
      }]
      : []),
    {
      icon: <IconUser size={18} />,
      label: 'Member record',
      value: isMatched
        ? (
          <Badge size="sm" variant="light" color="teal">
            {person.memberName || `Member #${person.memberID}`}
          </Badge>
        )
        : (
          <Badge size="sm" variant="light" color="orange">
            Created when the {contextLabel} is approved
          </Badge>
        ),
    },
  ];

  const notice = person.nameOnly ? (
    // ⚠️ Not a "no data" message — the data was collected, the admin API just does not return it.
    <Alert color="yellow" variant="light" icon={<IconInfoCircle size={16} />} p="xs">
      <Text size="sm">
        The {contextLabel} API returns only a name for the person being referred. Their contact
        details, gender and date of birth are not available here — use the requester's contact details
        to reach them.
      </Text>
    </Alert>
  ) : isMatched ? (
    <Alert color="teal" variant="light" icon={<IconInfoCircle size={16} />} p="xs">
      <Text size="sm">
        Matched to an existing member{person.memberName ? `, ${person.memberName}` : ''}. The details
        below are what {submittedName || 'they'} submitted, which may differ from the saved record.
      </Text>
    </Alert>
  ) : (
    <Alert color="gray" variant="light" icon={<IconUserQuestion size={16} />} p="xs">
      <Text size="sm">
        Not matched to an existing member. These are the details {submittedName || 'they'} gave on the
        public form; the member record is created when the {contextLabel} is approved.
      </Text>
    </Alert>
  );

  return (
    <MemberDetailModal
      member={fields}
      opened={opened}
      onClose={onClose}
      contextLabel={contextLabel}
      notice={notice}
      attachmentLabel={`On this ${contextLabel}`}
      rows={rows}
      // Always "Person Details", even when matched: the grid shows what was **submitted**, not the
      // saved member record, so calling it "Member Details" would misdescribe it.
      title="Person Details"
    />
  );
}
